/**
 * Documentation registry.
 *
 * Ports the file-based docs architecture to CRA. A page's `section` is the only
 * thing that places it in the sidebar, the index and search — add an entry here
 * and it appears everywhere, no separate nav to maintain.
 *
 * SECTIONS is a closed list on purpose: a typo like "Getting started" would
 * otherwise silently create a second half-empty sidebar group. validateDocs()
 * turns that into a loud console error in development instead.
 */

export const SECTIONS = [
  'Getting Started',
  'Company Directory',
  'Verification & Tiers',
  'Letters of Intent',
  'Quote Requests',
  'For Administrators',
  'Reference',
];

// Inline stroke-SVG paths — no icon dependency, and they inherit currentColor
// so they theme for free.
export const SECTION_ICONS = {
  'Getting Started': 'M13 10V3L4 14h7v7l9-11h-7z',
  'Company Directory': 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  'Verification & Tiers': 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
  'Letters of Intent': 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  'Quote Requests': 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  'For Administrators': 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  Reference: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
};

/**
 * Every page. `body` is a function returning JSX so pages stay lazy-free but
 * still get the Shot/Callout components injected by the renderer.
 */
export const DOCS = [
  // ---------------------------------------------------------- Getting Started
  {
    slug: 'what-is-trados',
    title: 'What is Trados',
    description: 'The verified network for global commodity trade — what the platform does and who it is for.',
    section: 'Getting Started',
    order: 0,
    updated: '2026-08-01',
  },
  {
    slug: 'creating-your-account',
    title: 'Creating your account',
    description: 'Register, verify your email address, and get access to contact details across the directory.',
    section: 'Getting Started',
    order: 1,
    updated: '2026-08-01',
  },
  {
    slug: 'account-types',
    title: 'Account types and roles',
    description: 'Buyer, seller, broker and the difference between a user, a company admin and a super admin.',
    section: 'Getting Started',
    order: 2,
    updated: '2026-08-01',
  },

  // -------------------------------------------------------- Company Directory
  {
    slug: 'directory-overview',
    title: 'Browsing the directory',
    description: 'Search 1,500+ commodity trading companies by name, country and the commodities they trade.',
    section: 'Company Directory',
    order: 0,
    updated: '2026-08-01',
  },
  {
    slug: 'claiming-your-business',
    title: 'Claiming your business',
    description: 'Find your company in the directory and claim it so you can manage the listing yourself.',
    section: 'Company Directory',
    order: 1,
    updated: '2026-08-01',
  },
  {
    slug: 'edit-business-info',
    title: 'Editing your business information',
    description: 'Company name, logo, address, about text and the commodities you trade — all in one place.',
    section: 'Company Directory',
    order: 2,
    updated: '2026-08-01',
  },
  {
    slug: 'commodities-and-minerals',
    title: 'Commodities and minerals',
    description: 'How the commodity and mineral catalogues work and why picking from the list matters.',
    section: 'Company Directory',
    order: 3,
    updated: '2026-08-01',
  },
  {
    slug: 'network-badge',
    title: 'The Trados network badge',
    description: 'Embed your verification medal on your own website so visitors can confirm your status.',
    section: 'Company Directory',
    order: 4,
    updated: '2026-08-01',
  },

  // ----------------------------------------------------- Verification & Tiers
  {
    slug: 'verification-overview',
    title: 'How verification works',
    description: 'Bronze, Silver and Gold — what each tier checks and what it unlocks on the platform.',
    section: 'Verification & Tiers',
    order: 0,
    updated: '2026-08-01',
  },
  {
    slug: 'tier-bronze',
    title: 'Bronze verification',
    description: 'Identity basics — who you are, where you operate and how to reach you.',
    section: 'Verification & Tiers',
    order: 1,
    updated: '2026-08-01',
  },
  {
    slug: 'tier-silver-gold',
    title: 'Silver and Gold verification',
    description: 'Company details, incorporation documents and full KYC/KYB checks.',
    section: 'Verification & Tiers',
    order: 2,
    updated: '2026-08-01',
  },

  // -------------------------------------------------------- Letters of Intent
  {
    slug: 'create-loi',
    title: 'Creating a Letter of Intent',
    description: 'Generate a verifiable LOI that any counterparty can authenticate against Trados.',
    section: 'Letters of Intent',
    order: 0,
    updated: '2026-08-01',
  },
  {
    slug: 'verify-loi',
    title: 'Verifying a Letter of Intent',
    description: 'Check whether an LOI is genuine, and read the verification grade and report.',
    section: 'Letters of Intent',
    order: 1,
    updated: '2026-08-01',
  },

  // ------------------------------------------------------------ Quote Requests
  {
    slug: 'quote-requests',
    title: 'Requesting a quote',
    description: 'Submit a request, work through the process steps and track approvals.',
    section: 'Quote Requests',
    order: 0,
    updated: '2026-08-01',
  },

  // -------------------------------------------------------- For Administrators
  {
    slug: 'admin-directory',
    title: 'Managing the directory',
    description: 'Approve claims, edit listings, import companies and use the CSV-style data view.',
    section: 'For Administrators',
    order: 0,
    updated: '2026-08-01',
  },
  {
    slug: 'admin-verifications',
    title: 'Reviewing verifications',
    description: 'Work the tier submission queue and approve or reject with a reason.',
    section: 'For Administrators',
    order: 1,
    updated: '2026-08-01',
  },

  // ------------------------------------------------------------------ Reference
  {
    slug: 'directory-api',
    title: 'Directory API',
    description: 'Read, insert and update directory listings programmatically, with duplicate protection.',
    section: 'Reference',
    order: 0,
    updated: '2026-08-01',
  },
];

/** Loud in development if a page names a section that does not exist. */
export function validateDocs() {
  const bad = DOCS.filter((d) => !SECTIONS.includes(d.section));
  if (bad.length) {
    // eslint-disable-next-line no-console
    console.error('[docs] unknown section on:', bad.map((d) => `${d.slug} -> "${d.section}"`));
  }
  const dupes = DOCS.map((d) => d.slug).filter((s, i, a) => a.indexOf(s) !== i);
  if (dupes.length) {
    // eslint-disable-next-line no-console
    console.error('[docs] duplicate slugs:', dupes);
  }
  return bad.length === 0 && dupes.length === 0;
}

export const getDoc = (slug) => DOCS.find((d) => d.slug === slug);

/** Docs grouped by section, in editorial order, sorted by `order` then title. */
export function groupedDocs() {
  return SECTIONS.map((section) => ({
    section,
    icon: SECTION_ICONS[section],
    pages: DOCS.filter((d) => d.section === section)
      .sort((a, b) => (a.order - b.order) || a.title.localeCompare(b.title)),
  })).filter((g) => g.pages.length > 0);
}
